import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const userEmail = params.get('user_email');
    const userName = params.get('user_name');
    const userRole = params.get('user_role');

    if (accessToken && refreshToken) {
      localStorage.setItem('user_email', userEmail || '');
      localStorage.setItem('user_name', userName || '');
      localStorage.setItem('user_role', userRole || '');
      loginWithToken(accessToken, refreshToken);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => navigate('/'), 500);
    }
  }, []);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      window.location.href = `${API_BASE_URL}/api/v1/auth/google/login`;
    } catch (err: any) {
      setError(err.message || 'Failed to connect to authentication service.');
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Left Panel */}
      <div className="login-left">
        {/* Brand */}
        <div className="login-brand">
          <div className="brand-icon">A</div>
          <span className="brand-name">AssureX</span>
        </div>

        {/* Heading */}
        <div className="login-heading">
          <h2>Welcome Back!</h2>
          <p>Sign in to access your dashboard and continue optimizing your QA Test suites.</p>
        </div>

        {/* Error */}
        {error && (
          <div className="error-message">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
            </svg>
            {error}
          </div>
        )}

        <div className="login-form">
          {/* Google */}
          <button className="btn-social" onClick={handleGoogleLogin} disabled={isLoading}>
            {isLoading ? (
              <>
                <svg className="spinner" width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50" strokeDashoffset="25"/>
                </svg>
                Signing in...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>
        </div>
      </div>

      {/* Right Panel */}
      <div className="login-right">
        {/* Floating background icons */}
        <div className="floating-icons">
          <span className="float-icon fi-1">🐛</span>
          <span className="float-icon fi-2">✅</span>
          <span className="float-icon fi-3">🔍</span>
          <span className="float-icon fi-4">⚙️</span>
          <span className="float-icon fi-5">🧪</span>
          <span className="float-icon fi-6">📋</span>
          <span className="float-icon fi-7">🚀</span>
          <span className="float-icon fi-8">🔧</span>
          <span className="float-icon fi-9">⚡</span>
          <span className="float-icon fi-10">🛡️</span>
        </div>

        <div className="right-content">
          <h1 className="right-headline">Revolutionize QA with Smarter Test Management</h1>
          <p className="right-subheadline">Streamline QA Test Suites. Ship with Confidence.</p>
        </div>
      </div>
    </div>
  );
};
